<template>
  <div>
    <!-- ── EXPIRED BANNER (top of page) ── -->
    <div v-if="!isLoading">
      <div
        v-if="(accountStatus === 'inactive' || (accountStatus === 'trial' && trialDaysLeft <= 0)) && !canView('Settings')"
        class="expired-banner-top">
        Your account has expired, please contact your systems administrator.
      </div>
    </div>

    <!-- ── MOBILE TOP BAR ── -->
    <div class="mobile-top-bar">
      <button class="hamburger-btn" @click="toggleMobileMenu" :class="{ 'active': isMobileMenuOpen }">
        <span></span><span></span><span></span>
      </button>
      <router-link :to="{ name: 'dashboard' }" class="mobile-logo-link">
        <img class="mobile-logo" :src="orgLogoUrl || defaultLogo" alt="Logo" />
        <span class="mobile-logo-text" :title="brandName">{{ brandName }}</span>
      </router-link>
    </div>

    <!-- ── MOBILE DROPDOWN MENU ── -->
    <div class="mobile-menu" :class="{ 'open': isMobileMenuOpen }">
      <nav class="mobile-nav" v-if="user">

        <router-link v-if="canView('Dashboard')" :to="{ name: 'dashboard' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          <span class="nav-text">Dashboard</span>
        </router-link>

        <router-link v-if="canView('News')" :to="{ name: 'comms' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
          <span class="nav-text">Comms</span>
          <span
            v-if="commsUnreadLabel"
            class="nav-unread"
            :aria-label="commsUnreadAria"
          >{{ commsUnreadLabel }}</span>
        </router-link>

        <router-link v-if="canView('Schedule')" :to="{ name: 'schedule' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span class="nav-text">Schedule</span>
        </router-link>

        <router-link v-if="canView('Locations')" :to="{ name: 'locations' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <span class="nav-text">Locations</span>
        </router-link>

        <router-link v-if="canView('People')" :to="{ name: 'people' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          <span class="nav-text">People</span>
        </router-link>

        <!-- Timesheet Dropdown -->
        <div v-if="hasAnyTimesheetView()" class="mobile-nav-item utilities-parent" :class="{ 'active': isTimesheetActive, 'expanded': showTimesheetDropdown }" @click="toggleTimesheetDropdown">
          <div class="nav-item-content">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span class="nav-text">Timesheet</span>
            <svg class="dropdown-chev" :class="{ 'rotated': showTimesheetDropdown }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
        <div class="utilities-submenu" v-if="showTimesheetDropdown">
          <router-link v-if="canView('Timesheets')" :to="{ name: 'timesheets' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span class="nav-text">Approve Timesheets</span>
          </router-link>
          <router-link v-if="canView('Export Timesheets')" :to="{ name: 'export-timesheets' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            <span class="nav-text">Export Timesheets</span>
          </router-link>
          <!-- T6: BOTH CONDITIONS, AND THEY ARE DIFFERENT QUESTIONS. The
               capability decides whether this person may feed payroll; the
               connection decides whether there is a payroll to feed. Either
               one alone renders an entry that leads somewhere useless. -->
          <router-link v-if="canView('Export Timesheets') && xeroConnected" :to="{ name: 'send-to-xero' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <span class="nav-text">Send to Xero</span>
          </router-link>
        </div>

        <!-- Utilities Dropdown -->
        <div v-if="hasAnyUtilityView()" class="mobile-nav-item utilities-parent" :class="{ 'active': isUtilitiesActive, 'expanded': showUtilitiesDropdown }" @click="toggleUtilitiesDropdown">
          <div class="nav-item-content">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
            <span class="nav-text">Utilities</span>
            <svg class="dropdown-chev" :class="{ 'rotated': showUtilitiesDropdown }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
        <div class="utilities-submenu" v-if="showUtilitiesDropdown">
          <router-link v-if="canView('Reports')" :to="{ name: 'reports' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            <span class="nav-text">Reports</span>
          </router-link>
          <router-link v-if="canView('Forms')" :to="{ name: 'forms' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span class="nav-text">Form Builder</span>
          </router-link>
          <router-link v-if="canView('Templates')" :to="{ name: 'templates' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            <span class="nav-text">Templates</span>
          </router-link>
          <router-link v-if="canView('Submitted')" :to="{ name: 'submitted' }" class="mobile-nav-item submenu-item" @click="closeMobileMenu">
            <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span class="nav-text">Submitted</span>
          </router-link>
        </div>

        <!--
          INTEGRATIONS (T1). ⚠ THIS ENTRY EXISTS TWICE — here in the mobile nav
          and again in the desktop sidebar. They are independent markup, so an
          entry added to one and not the other is invisible on the other with
          nothing failing. Asserted in BOTH by
          src/tests/integrationsNavAndRoute.test.js, whose name says why.

          `canView('Export Timesheets')` and NOT a new view name: D-C. The
          route is reachable because viewNameToRouteMap maps that one view to
          BOTH routes — see src/router/index.js. A new view here would show the
          entry and let the guard bounce it, which is the CheckoutResults
          failure.
        -->
        <router-link v-if="canView('Export Timesheets')" :to="{ name: 'integrations' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 2v6M15 2v6"/><path d="M6 8h12v4a6 6 0 01-12 0z"/><path d="M12 18v4"/></svg>
          <span class="nav-text">Integrations</span>
        </router-link>

        <router-link v-if="canView('Settings')" :to="{ name: 'OrgSettings' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>
          <span class="nav-text">Settings</span>
        </router-link>

        <!-- H1 (2026-11-01): Support → Help & support; same position, icon and permission view ('Support'). -->
        <router-link v-if="canView('Support')" :to="{ name: 'help' }" class="mobile-nav-item" @click="closeMobileMenu">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span class="nav-text">Help &amp; support</span>
        </router-link>

        <div class="mobile-nav-item" @click="handleMobileLogout">
          <svg class="mob-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          <span class="nav-text">Logout</span>
        </div>
      </nav>
    </div>

    <!-- ── DESKTOP SIDEBAR ── -->
    <div class="sidebar desktop-only" :class="{ 'collapsed': isCollapsed }">

      <!-- LOGO -->
      <div class="sidebar-header">
        <div v-if="isCollapsed" @click="toggleSidebar" class="logo-link clickable" :title="'Expand Menu'">
          <img class="nav-logo" :src="orgLogoUrl || defaultLogo" alt="Logo" />
        </div>
        <router-link v-else :to="{ name: 'dashboard' }" class="logo-link">
          <img class="nav-logo" :src="orgLogoUrl || defaultLogo" alt="Logo" />
          <span class="logo-text" :title="brandName">{{ brandName }}</span>
        </router-link>
        <button v-if="!isCollapsed" class="toggle-btn" @click="toggleSidebar" title="Collapse sidebar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      </div>

      <!-- NAV -->
      <nav class="sidebar-nav" v-if="user">

        <div class="sb-section" v-if="!isCollapsed">Main</div>

        <router-link v-if="canView('Dashboard')" :to="{ name: 'dashboard' }" class="sb-item" :title="isCollapsed ? 'Dashboard' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          <span class="label" v-if="!isCollapsed">Dashboard</span>
        </router-link>

        <router-link v-if="canView('News')" :to="{ name: 'comms' }" class="sb-item" :title="isCollapsed ? 'Comms' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
          <span class="label" v-if="!isCollapsed">Comms</span>
          <!-- ⚠ RENDERED WHETHER OR NOT THE RAIL IS COLLAPSED, unlike the label.
               A collapsed rail is the state somebody leaves the app in all day,
               and a badge that disappears with the words is a badge that is
               absent exactly when it is the only thing left to notice. -->
          <span
            v-if="commsUnreadLabel"
            class="nav-unread"
            :class="{ 'nav-unread-collapsed': isCollapsed }"
            :aria-label="commsUnreadAria"
          >{{ commsUnreadLabel }}</span>
        </router-link>

        <router-link v-if="canView('Schedule')" :to="{ name: 'schedule' }" class="sb-item" :title="isCollapsed ? 'Schedule' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span class="label" v-if="!isCollapsed">Schedule</span>
        </router-link>

        <router-link v-if="canView('Locations')" :to="{ name: 'locations' }" class="sb-item" :title="isCollapsed ? 'Locations' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <span class="label" v-if="!isCollapsed">Locations</span>
        </router-link>

        <router-link v-if="canView('People')" :to="{ name: 'people' }" class="sb-item" :title="isCollapsed ? 'People' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          <span class="label" v-if="!isCollapsed">People</span>
        </router-link>

        <!-- Timesheet Dropdown -->
        <div v-if="hasAnyTimesheetView()" class="sb-item utilities-parent" :class="{ 'active': isTimesheetActive }" @click="toggleTimesheetDropdown" :title="isCollapsed ? 'Timesheet' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span class="label" v-if="!isCollapsed">Timesheet</span>
          <svg v-if="!isCollapsed" class="dropdown-chev" :class="{ 'rotated': showTimesheetDropdown }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        <div class="utilities-submenu" v-if="showTimesheetDropdown && !isCollapsed">
          <router-link v-if="canView('Timesheets')" :to="{ name: 'timesheets' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span class="label">Approve Timesheets</span>
          </router-link>
          <router-link v-if="canView('Export Timesheets')" :to="{ name: 'export-timesheets' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            <span class="label">Export Timesheets</span>
          </router-link>
          <!-- T6: the same two conditions as the mobile render, and the same
               reason. ONE component, TWO renders — not two components that can
               come to disagree about whether Xero is connected. -->
          <router-link v-if="canView('Export Timesheets') && xeroConnected" :to="{ name: 'send-to-xero' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <span class="label">Send to Xero</span>
          </router-link>
        </div>

        <div class="sb-section" v-if="!isCollapsed">Manage</div>

        <!-- Utilities Dropdown -->
        <div v-if="hasAnyUtilityView()" class="sb-item utilities-parent" :class="{ 'active': isUtilitiesActive }" @click="toggleUtilitiesDropdown" :title="isCollapsed ? 'Utilities' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
          <span class="label" v-if="!isCollapsed">Utilities</span>
          <svg v-if="!isCollapsed" class="dropdown-chev" :class="{ 'rotated': showUtilitiesDropdown }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        <div class="utilities-submenu" v-if="showUtilitiesDropdown && !isCollapsed">
          <router-link v-if="canView('Reports')" :to="{ name: 'reports' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            <span class="label">Reports</span>
          </router-link>
          <router-link v-if="canView('Forms')" :to="{ name: 'forms' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span class="label">Form Builder</span>
          </router-link>
          <router-link v-if="canView('Templates')" :to="{ name: 'templates' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            <span class="label">Templates</span>
          </router-link>
          <router-link v-if="canView('Submitted')" :to="{ name: 'submitted' }" class="sb-item submenu-item">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span class="label">Submitted</span>
          </router-link>
        </div>

        <!-- INTEGRATIONS (T1). The desktop half of the pair — see the note on
             the mobile entry above for why both exist and what breaks if only
             one is edited. -->
        <router-link v-if="canView('Export Timesheets')" :to="{ name: 'integrations' }" class="sb-item" :title="isCollapsed ? 'Integrations' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 2v6M15 2v6"/><path d="M6 8h12v4a6 6 0 01-12 0z"/><path d="M12 18v4"/></svg>
          <span class="label" v-if="!isCollapsed">Integrations</span>
        </router-link>

        <router-link v-if="canView('Settings')" :to="{ name: 'OrgSettings' }" class="sb-item" :title="isCollapsed ? 'Settings' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>
          <span class="label" v-if="!isCollapsed">Settings</span>
        </router-link>

        <router-link v-if="canView('Support')" :to="{ name: 'help' }" class="sb-item" :title="isCollapsed ? 'Help & support' : ''">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span class="label" v-if="!isCollapsed">Help &amp; support</span>
        </router-link>

        <!--
          D3 (2026-10-13) — the "Coming soon / Xero integration" block was
          removed, not reworded. It was the only thing the block ever held, and
          it had become false: a venue can connect Xero from Export Timesheets
          and see the organisation it is connected to. Nothing was put in its
          place because the alternative is announcing something nobody has
          decided to announce, and the connect panel on that screen is already
          the specific, true statement of what the integration does today.
        -->

      </nav>

      <!-- FOOTER -->
      <div class="sidebar-footer">

        <!-- Trial / Expired banner -->
        <div class="trial-banner-bottom" :class="{ 'collapsed-hidden': isCollapsed }">
          <div v-if="!isLoading" class="trial-banner-content">
            <div v-if="canView('Settings')">
              <div v-if="accountStatus === 'trial' && trialDaysLeft > 0" class="trial-info">
                <SubscriptionCountdown class="trial-days" :endsAt="trialEndsAtRaw" kind="trial" :now="now" />
                <button class="subscribe-btn-small" @click="showSubscribePopup = true">Subscribe now</button>
              </div>
              <!-- P2 (2026-09-19): a subscription scheduled to end. Same component and
                   same slot as the trial countdown (D6). It ends on a date; it is never
                   "cancelled" (D5).
                   Follow-up (2026-09-19): these buttons call the SAME functions as the
                   plan card's (useSubscriptionActions). They were router-links to
                   OrgSettings?tab=plan, a query nothing reads, so they did nothing. -->
              <div v-else-if="accountStatus === 'active' && subscriptionEndsAtRaw" class="trial-info">
                <SubscriptionCountdown class="trial-days" :endsAt="subscriptionEndsAtRaw" kind="ending" :now="now" />
                <button class="subscribe-btn-small nb-keep" :disabled="billingBusy" @click="keepSubscription">
                  {{ billingBusy ? 'Working…' : 'Keep subscription' }}
                </button>
                <span v-if="billingError" class="nb-billing-error" role="alert">{{ billingError }}</span>
              </div>
              <!-- P2 (2026-09-19): past_due is let in, with a prompt to update the card
                   (Connor). Stripe is still retrying the payment. -->
              <div v-else-if="accountStatus === 'past_due'" class="expired-info">
                <span class="expired-text">Payment didn't go through</span>
                <button class="subscribe-btn-small nb-update-card" :disabled="billingBusy" @click="updateCard">
                  {{ billingBusy ? 'Working…' : 'Update card' }}
                </button>
                <span v-if="billingError" class="nb-billing-error" role="alert">{{ billingError }}</span>
              </div>
              <div v-else-if="accountStatus === 'inactive' || (accountStatus === 'trial' && trialDaysLeft <= 0)" class="expired-info">
                <span class="expired-text">Account expired</span>
                <button class="subscribe-btn-small" @click="showSubscribePopup = true">Subscribe now</button>
              </div>
            </div>
            <div v-else>
              <div v-if="(accountStatus === 'inactive' || (accountStatus === 'trial' && trialDaysLeft <= 0)) && !canView('Settings')" class="expired-info">
                <span class="expired-text-small">Account expired</span>
              </div>
            </div>
          </div>
        </div>

        <!-- User block -->
        <div class="sb-user" @click="showLogoutConfirmation = true" :title="isCollapsed ? 'Logout' : 'Click to log out'">
          <div class="sb-avatar">{{ userInitials }}</div>
          <div v-if="!isCollapsed" class="sb-user-info">
            <div class="sb-user-name">{{ personName || user?.name || 'My Account' }}</div>
            <div class="sb-user-role">{{ uT.permissions || 'Log out' }}</div>
          </div>
          <svg v-if="!isCollapsed" class="sb-logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </div>

      </div>
    </div>

    <!-- ── LOGOUT CONFIRMATION ── -->
    <div v-if="showLogoutConfirmation" class="popup-container" @click.self="cancelLogout">
      <div class="popup-content dialog-confirm logout-dialog">
        <div class="d-header">
          <div class="d-icon-wrap" style="background:#f3f4f6;">
            <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </div>
          <div class="d-header-text">
            <div class="d-title">Log out?</div>
            <div class="d-body-text">Are you sure you want to log out of Rosterem?</div>
          </div>
        </div>
        <div class="d-footer right">
          <button class="dbtn dbtn-cancel" @click="cancelLogout">Cancel</button>
          <button class="dbtn dbtn-danger" @click="confirmLogout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Yes, Log out
          </button>
        </div>
      </div>
    </div>

    <!-- ── SUBSCRIBE POPUP ── -->
    <SubscribePopup
      v-if="showSubscribePopup"
      :visible="showSubscribePopup"
      :daysRemaining="trialDaysLeft !== null ? trialDaysLeft : 0"
      :accountStatus="accountStatus"
      :closeButton="closeButton"
      @closePopup="showSubscribePopup = false"
    />
  </div>
</template>

<script>
import { token, userToken } from "@/stores/token";
import { getDoc, doc, collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import defaultLogo from "@/assets/logo.svg";
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { projectFirestore } from "@/firebase/config";
import { trialDaysLeft as computeTrialDaysLeft } from "../../shared/trial";
import SubscribePopup from "@/components/SubscribePopup.vue";
import SubscriptionCountdown from "@/components/SubscriptionCountdown.vue";
import { useSubscriptionActions } from "@/composables/useSubscriptionActions";
import { useAuth0 } from '@auth0/auth0-vue';
import { useRoute } from 'vue-router';
import commsUnreadStore from '@/stores/commsUnreadStore';
import { brandNameForOrg, setOrgBrandName } from '@/utils/orgBrandName';

export default {
  name: "SidebarNav",
  components: {
    SubscribePopup,
    SubscriptionCountdown,
  },
  setup() {
    const { user, logout } = useAuth0();

    const route = useRoute();
    const uT = userToken();
    const store = token();

    // ███ THE COMMS UNREAD BADGE ███ (T3, 2026-10-27)
    //
    // ⚠ THE NAV ADDS NO LISTENER. `commsUnreadStore.start` is idempotent and
    // `CommsChat.vue` calls it too; whichever mounts first attaches the three
    // listeners and the other returns. The badge reads a computed, so opening
    // the chat does not double anything.
    //
    // ⚠ IT IS STARTED HERE AND NOT IN `CommsChat.vue` ALONE, because the number
    // has to be right on the Dashboard — a badge that only appears once you
    // visit the page it is telling you to visit is not a notification.
    //
    // ⚠ AND IT IS GATED BY NEITHER `canView` NOR A ROUTE. The template's own
    // `v-if="canView('News')"` already decides whether the entry exists; the
    // store attaches for anybody with an organisation and a uid, and its own
    // read rules decide what they can see. Gating it again here would be a
    // second permission model for the same data.
    const startCommsUnread = () => {
      commsUnreadStore.start({
        organisationId: store.organisationId || '',
        uid: user.value && typeof user.value.sub === 'string' ? user.value.sub : '',
      });
    };
    startCommsUnread();
    watch(() => [store.organisationId, user.value && user.value.sub], startCommsUnread);

    const commsUnreadLabel = commsUnreadStore.totalLabel;
    const commsUnreadTotal = commsUnreadStore.total;
    // Spelled out for a screen reader: a bare "3" beside "Comms" is not a
    // statement, and the capped "99+" is not even a number.
    const commsUnreadAria = computed(() => (commsUnreadTotal.value === 1
      ? '1 unread message'
      : `${commsUnreadTotal.value} unread messages`));

    const trialDaysLeft = ref(null);
    const accountStatus = ref("");
    const orgLogoUrl = ref(null);
    // B1 (2026-10-29): the word beside the logo, desktop and mobile bar alike.
    // `organisation.brandName`, absent → "Rosterem" (utils/orgBrandName).
    const brandName = computed(() => brandNameForOrg(store.organisationId));
    const isLoading = ref(true);
    const personName = ref('');
    const showSubscribePopup = ref(false);
    const closeButton = true;
    const showLogoutConfirmation = ref(false);
    const isCollapsed = ref(false);
    const showUtilitiesDropdown = ref(false);
    const showTimesheetDropdown = ref(false);
    const isMobileMenuOpen = ref(false);
    const allowedViews = ref([]);

    // ███ THE XERO CONNECTION, FOR THE "SEND TO XERO" ENTRY ███
    //
    // T6. The entry is hidden for a venue with no Xero, because a nav item
    // leading to a page that can only refuse is a promise the product cannot
    // keep — and this nav is the only way the page is found.
    //
    // ⚠ AN onSnapshot AND NOT A getDoc, deliberately. A venue connects Xero on
    // Integrations in the same session it then wants to send hours in, and the
    // Navbar is mounted the whole time: a one-shot read would leave the entry
    // missing until a reload, which is a UI state nobody on the screen can
    // clear. One listener on one document, for the life of the component.
    //
    // ⚠ AND IT STARTS AS `false`, so the entry is absent until the listener
    // says otherwise. An absent entry for a connected venue is a second's
    // delay; a present entry for a disconnected one is a dead link.
    const xeroConnected = ref(false);
    let unsubXero = null;

    const watchXeroConnection = () => {
      // Latched: `allowedViews` is fetched once, but a watcher that fires twice
      // would open a second listener on the same document and leak the first.
      if (unsubXero) return;
      if (!store.organisationId) return;
      unsubXero = onSnapshot(
        doc(projectFirestore, `organisations/${store.organisationId}/integrations/xero`),
        (snap) => { xeroConnected.value = snap.exists() && snap.data()?.connected === true; },
        // A denied or failed listener is TERMINAL in this SDK and never retries
        // (`memory/firestore-sdk-denied-read-behaviour.md`), so the entry stays
        // hidden rather than being left in whatever state it was last in.
        (error) => {
          console.error("Error watching Xero connection:", error);
          xeroConnected.value = false;
        },
      );
    };

    const utilityViews = ['Reports', 'Forms', 'Templates', 'Submitted'];

    const canView = (viewName) => {
      return allowedViews.value.includes(viewName);
    };

    // ███ AND THE LISTENER OPENS ONLY FOR SOMEBODY WHO COULD SEE THE ENTRY ███
    //
    // The Navbar is mounted for EVERY member, and most of them are staff with
    // no `Export Timesheets` capability: for them the entry can never render,
    // so a listener on the connection document is a billed read per session,
    // and a live listener for the whole session, for nothing. The same argument
    // that took the dead `integrations` listener out of
    // `src/views/ExportTimesheets.vue` in this ticket.
    //
    // ⚠ A WATCH AND NOT A CALL INSIDE `fetchAllowedViews`, because that
    // function has three exits — an early return with no organisation, a
    // success, and a catch — and a watch covers all three without the next
    // person having to notice there are three.
    //
    // ⚠ AND NOT `immediate`, for two reasons: `allowedViews` is `[]` at setup
    // so there is nothing to decide yet, and `canView` is declared below this
    // point — an immediate callback would read it before its initialiser and
    // throw.
    watch(allowedViews, () => {
      if (canView('Export Timesheets')) watchXeroConnection();
    });

    const hasAnyUtilityView = () => {
      return utilityViews.some(view => allowedViews.value.includes(view));
    };

    const hasAnyTimesheetView = () => {
      return ['Timesheets', 'Export Timesheets'].some(view => allowedViews.value.includes(view));
    };

    const fetchAllowedViews = async () => {
      try {
        const orgId = store.organisationId;
        const userPermissionName = uT.permissions;

        if (!orgId || !userPermissionName) {
          return;
        }

        const permissionsQuery = query(
          collection(projectFirestore, `organisations/${orgId}/permissions`),
          where("name", "==", userPermissionName)
        );

        const snapshot = await getDocs(permissionsQuery);

        if (!snapshot.empty) {
          const permissionData = snapshot.docs[0].data();
          allowedViews.value = permissionData.views || [];
        } else {
          allowedViews.value = [];
        }
      } catch (error) {
        console.error('❌ Error fetching allowed views:', error);
        allowedViews.value = [];
      }
    };

    const isUtilitiesActive = computed(() => {
      const utilitiesRoutes = ['reports', 'forms', 'templates', 'submitted'];
      return utilitiesRoutes.includes(route.name);
    });

    const isTimesheetActive = computed(() => {
      return ['timesheets', 'export-timesheets', 'send-to-xero'].includes(route.name);
    });

    const userInitials = computed(() => {
      const name = personName.value || user.value?.name;
      if (!name) return '?';
      const parts = name.trim().split(' ').filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    });

    const toggleSidebar = () => {
      isCollapsed.value = !isCollapsed.value;
      if (isCollapsed.value) {
        showUtilitiesDropdown.value = false;
        showTimesheetDropdown.value = false;
      }
      updateBodyClass();
    };

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false;
      showUtilitiesDropdown.value = false;
      showTimesheetDropdown.value = false;
    };

    const handleMobileLogout = () => {
      closeMobileMenu();
      showLogoutConfirmation.value = true;
    };

    const updateBodyClass = () => {
      if (isCollapsed.value) {
        document.body.classList.add('sidebar-collapsed');
        document.body.classList.remove('sidebar-expanded');
      } else {
        document.body.classList.add('sidebar-expanded');
        document.body.classList.remove('sidebar-collapsed');
      }
    };

    const toggleUtilitiesDropdown = () => {
      if (!isCollapsed.value || isMobileMenuOpen.value) {
        showUtilitiesDropdown.value = !showUtilitiesDropdown.value;
      }
    };

    const toggleTimesheetDropdown = () => {
      if (!isCollapsed.value || isMobileMenuOpen.value) {
        showTimesheetDropdown.value = !showTimesheetDropdown.value;
      }
    };

    const confirmLogout = async () => {
      logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    };

    const cancelLogout = () => {
      showLogoutConfirmation.value = false;
    };

    const fetchPersonName = async () => {
      try {
        const docSnap = await getDoc(doc(projectFirestore, `organisations/${store.organisationId}/people`, user.value.sub));
        if (docSnap.exists()) {
          const d = docSnap.data();
          personName.value = `${d.firstName || ''} ${d.lastName || ''}`.trim();
        }
      } catch {}
    };

    // Phase 1 (2026-08-17, trial-countdown correctness) — raw value kept so
    // recomputeTrialDaysLeft() can re-derive the displayed number without a
    // Firestore round trip (used by the visibilitychange listener below,
    // fixing the staleness bug: fetchTrialInfo used to run once on mount and
    // never again, so the count went stale by the age of a long-lived PWA
    // session). trialEndsAt is the ONLY source — no legacy `trial` string
    // fallback (removed once every dev/prod org was backfilled via
    // scripts/backfill-trial-ends-at.mjs). Missing/unparseable -> null,
    // handled entirely inside computeTrialDaysLeft.
    const trialEndsAtRaw = ref(null);
    // P2 (2026-09-19): when a scheduled cancel ends the subscription. Server-
    // written (in the firestore.rules blocklist), derived from Stripe's cancel_at.
    const subscriptionEndsAtRaw = ref(null);
    // The clock both countdowns read. SubscriptionCountdown only re-renders when
    // a prop changes, and `new Date()` is not reactive, so without this the
    // displayed number froze at mount: the staleness Phase 1 fixed for the trial
    // banner, reintroduced when P2 moved it into the component. Refreshed with
    // trialDaysLeft, on mount and on every return to the tab.
    const now = ref(new Date());

    const recomputeTrialDaysLeft = () => {
      now.value = new Date();
      trialDaysLeft.value = computeTrialDaysLeft(trialEndsAtRaw.value, now.value);
    };

    // The same actions the plan card calls (Checkout.vue), not a second copy.
    const {
      isLoading: billingBusy, errorMessage: billingError, keepSubscription, updateCard,
    } = useSubscriptionActions();

    const fetchTrialInfo = async () => {
      try {
        const orgDoc = await getDoc(
          doc(projectFirestore, `organisations/${store.organisationId}`)
        );
        if (orgDoc.exists()) {
          const data = orgDoc.data();
          accountStatus.value = data.accountStatus || "";
          orgLogoUrl.value = data.logoUrl || null;
          setOrgBrandName(store.organisationId, data.brandName);
          trialEndsAtRaw.value = data.trialEndsAt || null;
          subscriptionEndsAtRaw.value = data.subscriptionEndsAt || null;
          recomputeTrialDaysLeft();
        }
      } catch (error) {
        console.error("Error fetching trial info:", error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recomputeTrialDaysLeft();
      }
    };

    watch(() => route.path, () => {
      closeMobileMenu();
    });

    onMounted(() => {
      fetchAllowedViews();
      fetchTrialInfo();
      fetchPersonName();
      updateBodyClass();
      // ⚠ NO `watchXeroConnection()` HERE, deliberately: it is started by the
      // `allowedViews` watch above, once the capability is known. Calling it
      // here would open the listener for every member regardless.
      document.addEventListener('visibilitychange', handleVisibilityChange);
    });

    onUnmounted(() => {
      document.body.classList.remove('sidebar-collapsed', 'sidebar-expanded');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (unsubXero) unsubXero();
    });

    return {
      commsUnreadLabel,
      commsUnreadTotal,
      commsUnreadAria,
      user,
      uT,
      personName,
      trialDaysLeft,
      trialEndsAtRaw,
      subscriptionEndsAtRaw,
      now,
      billingBusy,
      billingError,
      keepSubscription,
      updateCard,
      accountStatus,
      isLoading,
      showSubscribePopup,
      closeButton,
      showLogoutConfirmation,
      isCollapsed,
      showUtilitiesDropdown,
      showTimesheetDropdown,
      isUtilitiesActive,
      isTimesheetActive,
      isMobileMenuOpen,
      allowedViews,
      xeroConnected,
      canView,
      hasAnyUtilityView,
      hasAnyTimesheetView,
      toggleSidebar,
      toggleMobileMenu,
      closeMobileMenu,
      handleMobileLogout,
      toggleUtilitiesDropdown,
      toggleTimesheetDropdown,
      confirmLogout,
      cancelLogout,
      orgLogoUrl,
      brandName,
      defaultLogo,
      userInitials,
    };
  },
};
</script>

<style scoped>
/* ── LOGOUT CONFIRMATION (centered text) ── */
.logout-dialog .d-header {
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.logout-dialog .d-header-text {
  text-align: center;
}
.logout-dialog .d-footer.right {
  justify-content: center;
}

/* ── EXPIRED BANNER ── */
.expired-banner-top {
  background-color: var(--orange);
  color: white;
  /* Phase-A1 (2026-08-04): top-edge banner, safe-area inset added to the
     top side only — left/right/bottom padding stay as they were. */
  padding: calc(10px + var(--safe-top)) 10px 10px;
  text-align: center;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  font-size: 13px;
}

/* ── MOBILE TOP BAR ── */
.mobile-top-bar {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Phase-A1 (2026-08-04): min-height (was a fixed height: 56px) + a
     safe-top top-padding — the bar grows downward on a notched device
     instead of squeezing its 56px of content under the notch.
     .mobile-menu's own top offset (below) is derived from this same
     56px + safe-top sum, so the dropdown still anchors flush underneath
     regardless of how much the bar actually grew. */
  min-height: 56px;
  background: var(--purple);
  align-items: center;
  padding: var(--safe-top) 1rem 0;
  z-index: 1001;
  box-shadow: 0 1px 0 rgba(255,255,255,0.07);
}

.hamburger-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 1002;
}

.hamburger-btn span {
  display: block;
  width: 22px;
  height: 2px;
  background: rgba(255,255,255,0.7);
  transition: all 0.25s ease;
  border-radius: 2px;
}

.hamburger-btn.active span:nth-child(1) { transform: rotate(45deg) translate(5px, 7px); }
.hamburger-btn.active span:nth-child(2) { opacity: 0; }
.hamburger-btn.active span:nth-child(3) { transform: rotate(-45deg) translate(5px, -7px); }

.mobile-logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-left: 12px;
  /* B1: a venue's name can be 24 characters; the link may shrink, not overflow. */
  min-width: 0;
}

.mobile-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 6px;
}

.mobile-logo-text {
  margin-left: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.02em;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* ── MOBILE MENU ── */
.mobile-menu {
  display: none;
  position: fixed;
  /* Phase-A1 (2026-08-04): .mobile-top-bar grew from a fixed 56px to
     56px + safe-top (min-height + padding-top, see above) — this offset
     tracks that so the dropdown still sits flush underneath, not
     overlapping the bar by safe-top's worth on a notched device. */
  top: calc(56px + var(--safe-top));
  left: 0;
  right: 0;
  background: var(--purple);
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.mobile-menu.open {
  /* Phase-A1 (2026-08-04): matches .mobile-menu's own top offset above. */
  max-height: calc(100vh - 56px - var(--safe-top));
  overflow-y: auto;
}

/* Phase-A1 (2026-08-04): bottom safe-area padding so the last nav item in
   an open dropdown isn't sitting under the home indicator. */
.mobile-nav { padding: 8px 0 calc(8px + var(--safe-bottom)); }

/* ███ THE COMMS UNREAD BADGE ███ (T3)
   One rule for both nav entries, because there are two controls for one number
   and a badge styled once per site is a badge that drifts. `margin-left: auto`
   puts it at the trailing edge of either row — both are flex rows — without
   either needing a spacer element. */
.nav-unread {
  margin-left: auto;
  flex-shrink: 0;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--secondary, #22c55e);
  color: #0b1f12;
  font-size: 11px;
  line-height: 18px;
  font-weight: 700;
  text-align: center;
}

/* A collapsed rail has no room for a pill beside an icon, so the badge becomes a
   dot. It keeps its `aria-label`, so the number is still announced where it is
   no longer drawn.

   ⚠ NOT ABSOLUTELY POSITIONED, deliberately. A dot over the icon's corner would
   need `position: absolute`, and `.sb-item` sets no `position` — so the offsets
   would resolve against whatever positioned ancestor happens to be above it in
   the tree. Adding `position: relative` to `.sb-item` to fix that would create a
   containing block for every other nav item's descendants at the same time. A
   flex item pushed to the trailing edge needs none of that. */
.nav-unread-collapsed {
  min-width: 8px;
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 50%;
  overflow: hidden;
  text-indent: -999px;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  padding: 12px 1.25rem;
  color: rgba(255,255,255,0.6) !important;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.mobile-nav-item:hover { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.85); }
.mobile-nav-item.router-link-active { background: rgba(34,197,94,0.12); color: var(--green-light) !important; }
.mobile-nav-item.router-link-active .mob-icon { color: var(--secondary); stroke: var(--secondary); opacity: 1; }

.mob-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.5;
  color: #fff;
  stroke: #fff;
  margin-right: 12px;
}
.mob-icon--img { opacity: 0.4; border-radius: 3px; }

.nav-text { font-size: 13px; font-family: 'Plus Jakarta Sans', sans-serif; }

.submenu-item { padding-left: 2.5rem; }
.utilities-submenu { background: rgba(0,0,0,0.12); }

.nav-item-content { display: flex; align-items: center; width: 100%; }

.dropdown-chev {
  width: 14px;
  height: 14px;
  margin-left: auto;
  opacity: 0.4;
  stroke: #fff;
  transition: transform 0.2s;
}
.dropdown-chev.rotated { transform: rotate(180deg); }

/* ── DESKTOP SIDEBAR ── */
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 230px;
  background: var(--purple);
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease;
  z-index: 1000;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.sidebar.collapsed { width: 64px; }

/* LOGO */
.sidebar-header {
  height: 56px;
  padding: 0 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  flex-shrink: 0;
}

.sidebar.collapsed .sidebar-header { justify-content: center; }

.logo-link {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex: 1;
  min-width: 0;
}

.logo-link.clickable { cursor: pointer; }

.nav-logo {
  width: 30px;
  height: 30px;
  object-fit: contain;
  border-radius: 7px;
  flex-shrink: 0;
}

.logo-text {
  font-size: 15px;
  font-weight: 600;
  color: #fff !important;
  letter-spacing: -0.02em;
  white-space: nowrap;
  /* B1: 24 characters is wider than the 230px sidebar leaves; end in an ellipsis. */
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.15s;
}
.toggle-btn:hover { color: rgba(255,255,255,0.7); background: none; border-color: transparent; }
.toggle-btn svg { width: 16px; height: 16px; stroke: rgba(255,255,255,0.3); }
.toggle-btn:hover svg { stroke: rgba(255,255,255,0.7); }

/* NAV */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem 0;
  scrollbar-width: none;
}
.sidebar-nav::-webkit-scrollbar { display: none; }

.sb-section {
  padding: 0.85rem 1.1rem 0.35rem;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255,255,255,0.22);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  justify-self: left;
}

/* NAV ITEMS */
.sb-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  margin: 1px 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.12s;
  text-decoration: none;
  border: 0.5px solid transparent;
  background: none;
  width: calc(100% - 16px);
  text-align: left;
  font-family: inherit;
}

.sb-item:hover { background: rgba(255,255,255,0.06); border-color: transparent; }
.sb-item.router-link-active,
.sb-item.active {
  background: rgba(34,197,94,0.13);
  border-color: rgba(34,197,94,0.22);
}

.sb-item .icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.45;
  color: #fff;
  stroke: #fff;
  min-width: 16px;
}
.sb-item.router-link-active .icon,
.sb-item.active .icon { opacity: 1; color: var(--secondary); stroke: var(--secondary); }

.sb-item .icon--img { opacity: 0.3; border-radius: 3px; }

.sb-item .label {
  font-size: 13px;
  color: rgba(255,255,255,0.5) !important;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
}
.sb-item.router-link-active .label,
.sb-item.active .label { color: var(--green-light) !important; font-weight: 500; }

.sb-item .dropdown-chev { color: rgba(255,255,255,0.3); stroke: rgba(255,255,255,0.3); }

/* Submenu */
.utilities-submenu { background: transparent; }
.submenu-item { padding-left: 20px; }
.submenu-item .icon { opacity: 0.35; }


/* FOOTER */
.sidebar-footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(255,255,255,0.07);
}

.trial-banner-bottom {
  padding: 0.75rem 1rem;
  transition: opacity 0.25s;
}
.trial-banner-bottom.collapsed-hidden { opacity: 0; pointer-events: none; height: 0; padding: 0; overflow: hidden; }

.trial-info, .expired-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.trial-days {
  font-size: 12px;
  color: var(--green-light);
  font-weight: 500;
}

.expired-text {
  font-size: 12px;
  font-weight: 500;
  color: var(--orange);
}

.expired-text-small {
  font-size: 11px;
  color: var(--orange);
}

.subscribe-btn-small {
  background: rgba(34,197,94,0.15);
  border: 1px solid rgba(34,197,94,0.25);
  color: var(--green-light);
  border-radius: var(--radius-sm);
  padding: 5px 10px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  font-family: inherit;
  text-align: center;
}
.nb-billing-error {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #fca5a5;
}
.subscribe-btn-small:hover { background: rgba(34,197,94,0.25); color: var(--green-light); border-color: rgba(34,197,94,0.35); }

/* USER BLOCK */
.sb-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.12s;
  border-radius: 0;
}
.sb-user:hover { background: rgba(255,255,255,0.06); }

.sb-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #052e16;
  flex-shrink: 0;
}

.sb-user-info { flex: 1; min-width: 0; }
.sb-user-name { font-size: 13px; color: rgba(255,255,255,0.7) !important; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sb-user-role { font-size: 11px; color: rgba(255,255,255,0.3) !important; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.sb-logout-icon { width: 14px; height: 14px; color: rgba(255,255,255,0.25); stroke: rgba(255,255,255,0.25); flex-shrink: 0; }

/* ── POPUP ── */
.popup-container {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.popup-content {
  background: #fff;
  padding: 28px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  max-width: 360px;
  width: 90%;
  text-align: center;
}

.popup-content p { font-size: 14px; color: var(--text-primary); margin-bottom: 0; }

.button-container {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 20px;
}

.button-red {
  background-color: var(--orange);
  border: 1px solid var(--orange);
  color: white;
}
.button-red:hover { background-color: var(--orange-light); border-color: var(--orange-light); color: white; }

/* ── COLLAPSED STATE ── */
.sidebar.collapsed .sb-section,
.sidebar.collapsed .label,
.sidebar.collapsed .logo-text,
.sidebar.collapsed .dropdown-chev,
.sidebar.collapsed .sb-user-info,
.sidebar.collapsed .sb-logout-icon { display: none; }

.sidebar.collapsed .sb-item {
  justify-content: center;
  padding: 9px;
  margin: 1px 8px;
  width: calc(100% - 16px);
}

.sidebar.collapsed .sb-user {
  justify-content: center;
  padding: 0.75rem;
}

/* ── MOBILE BREAKPOINT ── */
@media screen and (max-width: 768px) {
  .sidebar.desktop-only { display: none; }
  .mobile-top-bar { display: flex; }
  .mobile-menu { display: block; }
}
</style>
